/**
 * Labstep
 *
 * @module components/FilterPost/Report/Entity/Active/Post
 * @desc Active node component for entities
 */

import type { FilterPostActiveLabelProps } from 'labstep-web/components/FilterPost/Active/Label/types';
import { FilterPostReportEntity } from 'labstep-web/components/FilterPost/Report/Entity';
import { ICONS } from 'labstep-web/constants/icons';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { RemoveLabel } from 'labstep-web/core/Label/Remove';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { EntityUser } from 'labstep-web/models/entity-user.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadableShort,
} from 'labstep-web/services/postFilter/constants';
import type React from 'react';
import {
  getPostFilterEntity,
  getPostFilterEntityWithName,
} from '../../Form';

export const FilterPostReportEntityActivePost: React.FC<
  FilterPostActiveLabelProps
> = ({ node, onRemoveClick }) => {
  const PostFilterPathToEntityName = {
    'resourceItem.resource': Resource.entityName,
    resourceItem: ResourceItem.entityName,
    'experiment.protocol.collection': ProtocolCollection.entityName,
    'experiment.experimentWorkflow': ExperimentWorkflow.entityName,
    'experiment.experimentWorkflow.author': User.entityName,
  };

  const FILTERS = [
    getPostFilterEntity(
      'guid',
      PostFilterComparison.eq,
      'experiment.experimentWorkflow.author',
    ),
    getPostFilterEntity(
      'id',
      PostFilterComparison.eq,
      'experiment.experimentWorkflow',
    ),
    getPostFilterEntity(
      'id',
      PostFilterComparison.eq,
      'experiment.experimentWorkflow.entityState',
    ),
    getPostFilterEntity(
      'id',
      PostFilterComparison.eq,
      'resourceItem',
    ),
    getPostFilterEntity(
      'id',
      PostFilterComparison.eq,
      'experiment.protocol.collection',
    ),
    getPostFilterEntity(
      'guid',
      PostFilterComparison.eq,
      'experiment.protocol.collection',
    ),
    getPostFilterEntity(
      'id',
      PostFilterComparison.eq,
      'resourceItem.resource',
    ),
    getPostFilterEntityWithName(
      'id',
      PostFilterComparison.eq,
      'experiment.experimentWorkflow',
    ),
    getPostFilterEntityWithName(
      'id',
      PostFilterComparison.eq,
      'experiment.protocol.collection',
    ),
    getPostFilterEntityWithName(
      'id',
      PostFilterComparison.eq,
      'resourceItem',
    ),
    getPostFilterEntityWithName(
      'id',
      PostFilterComparison.eq,
      'resourceItem.resource',
    ),
  ];
  const { addPostFilter } = useParamsContext();
  const filterActive = FILTERS.find((filter) =>
    filter.isNodeActive(node, undefined, true),
  );
  if (!filterActive) {
    return null;
  }

  const path =
    filterActive
      .getAllPaths()
      .filter((p) => p !== 'protocolValues')[0] || '';

  let attributes = filterActive.getAttributeValues(node);
  const protocolValueName = attributes.name?.value;

  if (filterActive.getPredicates().length > 1) {
    attributes = Object.fromEntries(
      Object.entries(filterActive.getAttributeValues(node)).filter(
        ([key, value]) => key !== 'name',
      ),
    );
  }

  const entityName =
    PostFilterPathToEntityName[
      path as keyof typeof PostFilterPathToEntityName
    ];

  let label = getHumanReadableEntityName(entityName, false, true);

  const icon = ICONS[entityName as keyof typeof ICONS]?.primary;

  const comparison = PostFilterComparison.eq;
  const value = Object.values(attributes)[0].value;

  const comparaisonReadable =
    PostFilterComparisonHumanReadableShort[
      comparison as keyof typeof PostFilterComparisonHumanReadableShort
    ];

  const attributeName = Object.keys(attributes)[0];

  const params: Record<string, any> = {
    get_single: 1,
  };

  params[attributeName] = value;

  if (entityName === User.entityName) {
    label = 'Created by ';
  }

  let comparatorReadable = `${label} ${comparaisonReadable}`;

  if (entityName === User.entityName) {
    comparatorReadable = `Created by`;
  }

  return (
    <ReadOnMountHOC
      type="entities"
      entityName={entityName}
      params={params}
      loading={{ loader: 'show', cached: true }}
      children={({ entity }) => (
        <FilterPostReportEntity
          label={label}
          path={path}
          field={'id'}
          addPostFilter={addPostFilter}
          defaultValues={{}}
          viewComponent={({ toggleModal }) => (
            <RemoveLabel
              name={
                protocolValueName
                  ? `${protocolValueName}: ${comparatorReadable}: ${entity.name}`
                  : `${comparatorReadable}: ${entity.name}`
              }
              icon={icon}
              onRemoveClick={onRemoveClick}
            />
          )}
          entityName={entityName}
        />
      )}
    />
  );
};
